import { useTheme } from "expo-router/react-navigation";
import { Tag } from "lucide-react-native";
import {
  ComponentRef,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { View } from "react-native";

import { Histogram } from "@/components/charts";
import { Box, Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { HistogramDataPoint, QuestionResults } from "@/types/resources";
import ChartCopyButton from "./ChartCopyButton";
import { copyChartToClipboard } from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface PricingResultProps {
  compact?: boolean;
  result: QuestionResults;
}

/* ── Component ──────────────────────────────────────────── */

const PricingResult: FC<PricingResultProps> = ({ compact = false, result }) => {
  const { i18n, locale } = useLocalization();
  const theme = useTheme();
  const { groups } = result;

  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [chartCopied, setChartCopied] = useState(false);

  /* All numeric prices across the active groups (filtering nulls — the
     persona may decline to answer). The histogram bins are computed
     by `Histogram` itself from the raw `x` values. */
  const prices = useMemo(
    () =>
      groups
        .flatMap((g) => g.answers)
        .map(({ answer }) => (answer.type === "pricing" ? answer.price : null))
        .filter((p): p is number => p !== null && Number.isFinite(p)),
    [groups]
  );

  /* Pick the currency reported by the majority of answers — personas
     answering across mixed currencies is degenerate but we don't want
     a single noisy outlier to relabel the whole card. */
  const currency = useMemo<string | null>(() => {
    const counts = new Map<string, number>();
    groups
      .flatMap((g) => g.answers)
      .forEach(({ answer }) => {
        if (answer.type === "pricing" && answer.currency) {
          counts.set(answer.currency, (counts.get(answer.currency) ?? 0) + 1);
        }
      });
    let top: string | null = null;
    let topCount = 0;
    counts.forEach((count, code) => {
      if (count > topCount) {
        top = code;
        topCount = count;
      }
    });
    return top;
  }, [groups]);

  const totalRespondents = prices.length;

  const averagePrice = useMemo<number | null>(() => {
    if (prices.length === 0) return null;
    return prices.reduce((sum, p) => sum + p, 0) / prices.length;
  }, [prices]);

  const histogramData = useMemo<HistogramDataPoint[]>(
    () => prices.map((x) => ({ x })),
    [prices]
  );

  /* Locale-aware currency formatter shared by the average card and the
     histogram x-axis labels. Mirrors `PriceElasticityReport` so prices
     render the same way (e.g. "$12.50" / "12,50 €") wherever they
     appear in the app. Falls back to a plain decimal when the answer
     payload has no currency code. */
  const fmtPrice = useCallback(
    (value: number) => {
      if (!currency) {
        return new Intl.NumberFormat(locale, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(value);
      }
      return new Intl.NumberFormat(locale, {
        currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
      }).format(value);
    },
    [currency, locale]
  );

  const copyChart = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    await copyChartToClipboard(element, () => {
      setChartCopied(true);
      setTimeout(() => setChartCopied(false), 2000);
    });
  };

  const primary = theme.colors.primary;

  return (
    <VStack className="gap-3">
      {/* Banner — Average price. Mirrors the concept-testing winner
          banner in `MonadicReport`: colored icon tile on the left,
          contextual label in the middle, big price on the right. */}
      <Card
        className={`rounded-2xl ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
      >
        <HStack className="items-center gap-5">
          <Box
            className="w-14 h-14 rounded-2xl items-center justify-center shrink-0"
            style={{ backgroundColor: primary }}
          >
            <Tag color="white" size={26} strokeWidth={2.2} />
          </Box>
          <VStack className="flex-1">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {i18n.t("questions.averagePrice", { count: totalRespondents })}
            </Text>
          </VStack>
          <Text
            className="text-3xl font-bold tracking-tight"
            style={{ color: primary }}
          >
            {averagePrice !== null ? fmtPrice(averagePrice) : "—"}
          </Text>
        </HStack>
      </Card>

      {/* Chart — Price distribution. Histogram owns its own horizontal
          scrolling via the existing `needsScroll` threshold. */}
      <Card
        className={`group rounded-2xl ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 px-6 pt-6 pb-2"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={chartCopied}
          onPress={copyChart}
          textColor={theme.colors.text}
        />
        <VStack className="gap-6">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("dashboardNavigator.chartTitlePriceDistribution", {
              count: totalRespondents,
            })}
          </Text>
          <View ref={chartRef} style={{ width: "100%" }}>
            <Histogram data={histogramData} formatLabel={fmtPrice} />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default PricingResult;
